import { memo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { truncateMiddle } from '../../../../shared/lib/analytics-format'

/**
 * 기간 내 상위 링크.
 *
 * 막대는 1위를 기준으로 한 상대 길이다. 숫자만으로는 1위와 2위의 차이가
 * 얼마나 큰지 가늠되지 않는다.
 */

const TITLE_PREVIEW_LENGTH = 52

export interface TopLink {
  shortCode: string
  title: string
  clicks: number
  uniqueClicks: number
}

interface TopLinksListProps {
  links: TopLink[]
  /** 정밀 분석으로 넘어갈 때 현재 조건을 유지한다 */
  queryString: string
}

function TopLinksList({ links, queryString }: TopLinksListProps) {
  const { t } = useTranslation()
  const max = links.reduce((top, link) => Math.max(top, link.clicks), 0)

  return (
    <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <header className="flex flex-wrap items-center justify-between gap-3 px-6 pb-3 pt-5">
        <h3 className="text-[15px] font-bold text-gray-900 dark:text-white">
          {t('analyticsPage.topLinks')}
        </h3>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {t('analyticsPage.clickRowHint')}
        </span>
      </header>

      {links.length === 0 ? (
        <p className="px-6 py-10 text-sm text-gray-500 dark:text-gray-400">
          {t('analyticsPage.noLinksClicked')}
        </p>
      ) : (
        <ul>
          {links.map((link, index) => (
            <li key={link.shortCode}>
              <Link
                to={`/analytics/${link.shortCode}?${queryString}`}
                className="flex items-center gap-3.5 border-t border-gray-100 px-6 py-3 transition hover:bg-primary-50/60 dark:border-gray-700 dark:hover:bg-primary-950/30"
              >
                <span className="w-5 shrink-0 text-[13px] font-bold tabular-nums text-gray-400 dark:text-gray-500">
                  {index + 1}
                </span>

                <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                      {truncateMiddle(link.title, TITLE_PREVIEW_LENGTH)}
                    </span>
                    <span className="shrink-0 text-xs font-semibold text-primary-600 dark:text-primary-400">
                      /{link.shortCode}
                    </span>
                  </span>
                  <span className="block h-1 overflow-hidden rounded-sm bg-gray-100 dark:bg-gray-700">
                    <span
                      className="block h-full rounded-sm bg-primary-600"
                      style={{
                        width: `${max > 0 ? Math.max(2, (link.clicks / max) * 100) : 0}%`,
                      }}
                    />
                  </span>
                </span>

                <span className="flex w-20 shrink-0 flex-col items-end tabular-nums">
                  <span className="text-[15px] font-bold text-gray-900 dark:text-white">
                    {link.clicks.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    {t('analyticsPage.uniqueShort', {
                      count: link.uniqueClicks.toLocaleString(),
                    })}
                  </span>
                </span>

                <ChevronRight className="h-4 w-4 shrink-0 text-gray-300 dark:text-gray-600" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export default memo(TopLinksList)
